import React, { useEffect, useMemo, useCallback, FC, useState } from 'react'
import { createPortal } from 'react-dom'
import { Box, Flex, Heading } from '@moonpig/launchpad-components'
import { styled } from '@moonpig/launchpad-utils'
import { useQuery } from '@moonpig/web-core-graphql'
import { DateTime } from 'luxon'
import { system as s } from '@moonpig/launchpad-system'
import { trackGAEvent } from '@moonpig/web-core-analytics'
import { Breadcrumbs } from '@moonpig/web-core-nav'
import { spacingPx } from '@moonpig/launchpad-theme'
import { FilterSource } from '../../types'
import { ResultsCountHeader } from '../ResultsCountHeader'
import { ReminderPrompt } from './ReminderPrompt'
import { ApplicableFilter, SearchFilter } from '../../services/types/services'
import { GetReminderPromptGQL } from '../../queries/getReminderPrompt'
import {
  GetReminderPromptQuery,
  GetReminderPromptQueryVariables,
} from '../../queries/__generated__/getReminderPrompt'

import {
  getReminderPromptAnswer,
  ReminderPromptAnswer,
  setReminderPromptAnswer,
} from './sessionStorage'
import { getFiltersById } from '../../store/helpers'
import { relationshipMap } from './relationshipMap'
import { useSearchStore } from '../../store/SearchStore'
import { generateBreadcrumbsClickGAEvent } from '../../analytics/GAEvents'
import { PROMPT_CLOSING_TIME_MS } from './utils'
import { ReminderBanner } from './ReminderBanner'

const TrackableDiv = styled.div`
  flex: 0 0 auto;
  ${s({
    mb: 4,
  })}
`

const StyledGalleryHeader = styled(Flex)`
  flex-direction: row;
  align-items: center;

  ${({ theme }) =>
    s({
      pb: 5,
      gap: spacingPx(3)({ theme }),
    })}

  &.banner-visible {
    ${s({
      pb: { xs: 4, md: 3 },
    })}
  }
`
export type ReminderPromptHeaderProps = {
  title: string
  preAppliedFilters: SearchFilter[]
  selectedFilters: ApplicableFilter[]
  breadcrumbsItems: {
    label: string
    url: string
  }[]
  count: number
}

export const ReminderPromptHeader: FC<ReminderPromptHeaderProps> = ({
  title,
  selectedFilters,
  preAppliedFilters,
  breadcrumbsItems,
  count,
}) => {
  const [isBannerVisible, setIsBannerVisible] = useState(false)
  const [isPromptVisible, setIsPromptVisible] = useState(false)

  const toggleFilter = useSearchStore(store => store.toggleFilter)
  const togglePageLoading = useSearchStore(store => store.toggleLoading)
  const filters = useSearchStore(store => store.filters)

  const { startDate, endDate } = useMemo(() => {
    const now = DateTime.local()
    return {
      startDate: now.plus({ days: 1 }).toISODate(),
      endDate: now.plus({ days: 14 }).toISODate(),
    }
  }, [])

  const { data } = useQuery<
    GetReminderPromptQuery,
    GetReminderPromptQueryVariables
  >(GetReminderPromptGQL, {
    ssr: true,
    fetchPolicy: 'cache-first',
    variables: {
      remindersRequest: {
        startDate,
        endDate,
        take: 1,
      },
    },
  })

  const upcomingReminder = data?.reminders?.[0]

  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null)

  useEffect(() => {
    setPortalTarget(document.getElementById('gallery-fixed-bottom-bar-portal'))
  }, [])

  const toggleRelationshipFilter = useCallback(
    (select: boolean) => {
      // istanbul ignore next
      if (!upcomingReminder) {
        return
      }

      if (
        upcomingReminder.relationship &&
        relationshipMap[upcomingReminder.relationship]
      ) {
        const filterToToggle = getFiltersById({
          allFilters: filters,
          filterIds: relationshipMap[upcomingReminder.relationship],
        })[0]

        if (filterToToggle) {
          toggleFilter({
            filter: filterToToggle,
            select,
            source: FilterSource.ReminderPrompt,
          })
        }
      } else {
        // If there is no specific relationship filter to toggle, we trigger a loading state to
        // indicate that something is happening after the user opts in.
        togglePageLoading(true)
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        })
        setTimeout(() => {
          togglePageLoading(false)
        }, 1000)
      }
    },
    [filters, upcomingReminder, toggleFilter, togglePageLoading],
  )

  useEffect(() => {
    if (!upcomingReminder?.id) {
      return
    }
    const reminderPromptAnswer = getReminderPromptAnswer(upcomingReminder.id)

    if (reminderPromptAnswer === ReminderPromptAnswer.Yes) {
      setIsBannerVisible(true)
      toggleRelationshipFilter(true)
    } else if (!reminderPromptAnswer) {
      setIsPromptVisible(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upcomingReminder?.id])

  const handleBannerClose = useCallback(() => {
    setIsBannerVisible(false)
    toggleRelationshipFilter(false)
    setReminderPromptAnswer({
      answer: ReminderPromptAnswer.No,
      reminderId: upcomingReminder?.id as string,
    })
  }, [upcomingReminder?.id, toggleRelationshipFilter])

  const handlePromptAnswer = useCallback(
    (answer: ReminderPromptAnswer) => {
      setTimeout(() => setIsPromptVisible(false), PROMPT_CLOSING_TIME_MS)
      setReminderPromptAnswer({
        reminderId: upcomingReminder?.id as string,
        answer,
      })

      if (answer === ReminderPromptAnswer.Yes) {
        setIsBannerVisible(true)
        toggleRelationshipFilter(true)
      }
    },
    [upcomingReminder?.id, toggleRelationshipFilter],
  )

  return (
    <>
      {!isBannerVisible ? (
        <TrackableDiv
          onClick={() => {
            trackGAEvent(generateBreadcrumbsClickGAEvent(breadcrumbsItems))
          }}
          data-testid="breadcrumb-header"
        >
          <Breadcrumbs items={breadcrumbsItems} />
        </TrackableDiv>
      ) : null}
      <StyledGalleryHeader
        data-testid="reminder-prompt-header"
        className={isBannerVisible ? 'banner-visible' : ''}
      >
        <Box width="100%">
          {isBannerVisible && upcomingReminder ? (
            <ReminderBanner
              reminder={upcomingReminder}
              title={title}
              onClose={handleBannerClose}
            />
          ) : (
            <>
              <Heading
                display="inline"
                level="h1"
                typography={{
                  xs: 'typeMobileDisplay03',
                  md: 'typeDesktopDisplay03',
                }}
              >
                {title}
              </Heading>
              &nbsp;
              <ResultsCountHeader count={count} />
            </>
          )}
        </Box>
      </StyledGalleryHeader>
      {upcomingReminder &&
        isPromptVisible &&
        portalTarget &&
        createPortal(
          <ReminderPrompt
            reminder={upcomingReminder}
            preAppliedFilters={preAppliedFilters}
            selectedFilters={selectedFilters}
            onAnswer={handlePromptAnswer}
          />,
          portalTarget,
        )}
    </>
  )
}
